import type { Dispatch, SetStateAction } from "react";
import type { ProjectExportConfig } from "@iamu/shared";
import { WorkspacePrintDocument } from "./workspace-print-document";
import { WorkspaceFindingDrawer } from "./workspace-finding-drawer";
import { ZenFindingsCarousel } from "@/components/walkthrough/zen-mode";
import { WorkspaceDialogs } from "./workspace-dialogs";
import { WorkspaceBatchBar } from "./workspace-batch-bar";
import type {
  WalkthroughFinding,
  ActiveStepInfo,
  FindingKind,
} from "./workspace-types";
import type { WorkspaceTab } from "./use-workspace-search-params";

export interface WorkspaceOverlaysProps {
  walkthrough: any;
  allFindings: WalkthroughFinding[];
  walkthroughSteps: Array<{
    id: string;
    stepNumber: number;
    title: string;
    personaReasoning: string | null;
    grounding: string | null;
    screenshotPath: string | null;
    pageUrl?: string | null;
    surface?: string | null;
  }>;
  runNumber: number | undefined;
  verdicts: any;
  whatWorked: string[];
  openQuestions: string[];
  activeFinding: WalkthroughFinding | null;
  activeStep: ActiveStepInfo | null;
  projectId: string;
  canEdit: boolean;
  hasLinkedRepo: boolean;
  isTrackerConfigured: boolean;
  activeProvider: string | null;
  exportConfig?: ProjectExportConfig;
  mutations: any;
  share: any;
  reportOpen: boolean;
  setReportOpen: (open: boolean) => void;
  htmlReport: any;
  selectedIds: Set<string>;
  selectedCanonicalIds: string[];
  filteredFindings: WalkthroughFinding[];
  focusedIndex: number;
  kindFilter?: FindingKind;
  zenModal: { open: boolean; index: number };
  setZenModal: Dispatch<SetStateAction<{ open: boolean; index: number }>>;
  zenFindings: any[];
  workRequests: any;
  clearSelection: () => void;
  handleCloseDetail: () => void;
  setActiveTab: (tab: WorkspaceTab) => void;
  handleOpenZenMode: (index: number) => void;
}

export function WorkspaceOverlays({
  walkthrough,
  allFindings,
  walkthroughSteps,
  runNumber,
  verdicts,
  whatWorked,
  openQuestions,
  activeFinding,
  activeStep,
  projectId,
  canEdit,
  hasLinkedRepo,
  isTrackerConfigured,
  activeProvider,
  exportConfig,
  mutations,
  share,
  reportOpen,
  setReportOpen,
  htmlReport,
  selectedIds,
  selectedCanonicalIds,
  filteredFindings,
  focusedIndex,
  kindFilter,
  zenModal,
  setZenModal,
  zenFindings,
  workRequests,
  clearSelection,
  handleCloseDetail,
  setActiveTab,
  handleOpenZenMode,
}: WorkspaceOverlaysProps) {
  return (
    <>
      {/* Print Document */}
      <WorkspacePrintDocument
        walkthrough={walkthrough}
        allFindings={allFindings}
        walkthroughSteps={walkthroughSteps}
        runNumber={runNumber}
        verdicts={verdicts}
        whatWorked={whatWorked}
        openQuestions={openQuestions}
      />

      {/* Slide-over Finding Drawer */}
      <WorkspaceFindingDrawer
        activeFinding={activeFinding}
        activeStep={activeStep}
        walkthroughId={walkthrough.id}
        projectId={projectId}
        canEdit={canEdit}
        hasLinkedRepo={hasLinkedRepo}
        isTrackerConfigured={isTrackerConfigured}
        activeProvider={activeProvider}
        exportConfig={exportConfig}
        createIssuePending={mutations.createIssueMutation.isPending}
        onClose={handleCloseDetail}
        onCreateIssue={(finding) => {
          if (finding.canonicalFindingId) {
            mutations.setCreateIssueCanonicalId(finding.canonicalFindingId);
          } else {
            mutations.createIssueMutation.mutate({ findingId: finding.id });
          }
        }}
        onLinkExisting={(canonId) =>
          mutations.setLinkExistingCanonicalId(canonId)
        }
        onViewInTrace={() => setActiveTab("trace")}
        onOpenZen={() => {
          const idx = activeFinding
            ? filteredFindings.findIndex((f) => f.id === activeFinding.id)
            : focusedIndex;
          handleOpenZenMode(Math.max(0, idx));
        }}
      />

      {/* Fullscreen Zen Mode Carousel */}
      <ZenFindingsCarousel
        findings={zenFindings}
        initialIndex={zenModal.index}
        open={zenModal.open}
        onOpenChange={(open) => setZenModal((prev) => ({ ...prev, open }))}
        projectId={projectId}
        filterScope={kindFilter}
        canEdit={canEdit}
        hasLinkedRepo={hasLinkedRepo}
        isTrackerConfigured={isTrackerConfigured}
        activeProvider={activeProvider}
        exportConfig={exportConfig}
        createIssuePending={mutations.createIssueMutation.isPending}
        onCreateIssue={(finding) => {
          if (finding.canonicalFindingId) {
            mutations.setCreateIssueCanonicalId(finding.canonicalFindingId);
          } else {
            mutations.createIssueMutation.mutate({ findingId: finding.id });
          }
        }}
        onLinkExisting={(canonId) =>
          mutations.setLinkExistingCanonicalId(canonId)
        }
        onViewInTrace={() => {
          setZenModal({ open: false, index: 0 });
          setActiveTab("trace");
        }}
      />

      {/* Action Dialogs */}
      <WorkspaceDialogs
        projectId={projectId}
        scenarioName={walkthrough.scenario?.name}
        actionDialog={mutations.actionDialog}
        onCloseActionDialog={() => {
          mutations.setActionDialog(null);
          clearSelection();
        }}
        createIssueCanonicalId={mutations.createIssueCanonicalId}
        onCloseCreateIssue={() => mutations.setCreateIssueCanonicalId(null)}
        linkExistingCanonicalId={mutations.linkExistingCanonicalId}
        onCloseLinkExisting={() => mutations.setLinkExistingCanonicalId(null)}
        bulkIssuesOpen={mutations.bulkIssuesOpen}
        onCloseBulkIssues={() => {
          mutations.setBulkIssuesOpen(false);
          mutations.setBulkComparisonCanonicalIds(null);
        }}
        bulkCanonicalIds={
          mutations.bulkComparisonCanonicalIds ?? selectedCanonicalIds
        }
        reportOpen={reportOpen}
        onCloseReport={() => setReportOpen(false)}
        htmlReportContent={htmlReport?.content}
        isSharing={share.isSharing}
        shareInfo={share.shareInfo}
        onSharePublicLink={share.handleSharePublicLink}
        onDownloadPdf={share.handleDownloadPdf}
        onDownloadHtml={share.handleDownloadHtml}
      />

      {/* Floating Batch Bar */}
      {canEdit && (
        <WorkspaceBatchBar
          selectedCount={selectedIds.size}
          selectedCanonicalIds={selectedCanonicalIds}
          isTrackerConfigured={isTrackerConfigured}
          activeProvider={activeProvider}
          isPending={mutations.isTransitionPending}
          onClear={clearSelection}
          onOpenBulkIssues={() => {
            mutations.setBulkComparisonCanonicalIds(null);
            mutations.setBulkIssuesOpen(true);
          }}
          onOpenActionDialog={(action) =>
            mutations.setActionDialog({ action, ids: Array.from(selectedIds) })
          }
          onBulkReopen={() => mutations.handleBulkReopen(selectedIds)}
          onBulkDispatch={() => {
            if (selectedCanonicalIds.length === 0) return;
            workRequests.queueBulk(selectedCanonicalIds, "fix");
            clearSelection();
          }}
        />
      )}
    </>
  );
}
